import { create } from "zustand";
import { useConfigStore } from "./Config";
import { useRouterStore } from "./Router";
import { useSessionStore } from "./Session";

export type Article = {
  id?: string;
  _id?: string;
  createdAt?: Date;
  updatedAt?: Date;
  sentAt: Date;
  coverImageUrl: string;
  subject: string;
  message: any;
  subType?: string;
  config: {
    pinned?: boolean;
  };
  sender: {
    firstName?: string;
    lastName?: string;
    id?: string;
    profileImageUrl?: string;
  };
};

// The shared single-article endpoint serialises the Mongo document as-is,
// i.e. with `_id` but no `id`, while the list endpoint adds `id` explicitly.
// Home cards navigate with `news.id`, so a pinned article loaded through the
// single endpoint pushed `newsdetails` without an id and the detail view sat
// on its loading skeleton forever (#147330). Normalise both shapes here.
export const withArticleId = (article: any): Article | null => {
  if (!article || typeof article !== "object") {
    return null;
  }
  const id = article.id ?? article._id;
  return id ? { ...article, id: String(id) } : article;
};

interface NewsState {
  latestArticle?: Article;
  currentArticle?: Article;
  // Articles that home cards pin via config.articleId, keyed by article id.
  // `null` means "requested but unavailable" (draft, deleted or audience
  // mismatch) — the card stays hidden and the request is never repeated.
  pinnedArticles: { [id: string]: Article | null };
  articles: Article[];
  hasMore: boolean;
  loadingNews: boolean;
  page: number;
  setPage: (page: number) => void;
  loadNews: () => void;
  loadArticle: (id: string) => void;
  loadPinnedArticle: (id: string) => void;
}

export const useNewsStore = create<NewsState>()((set, get) => ({
  latestArticle: undefined,
  currentArticle: undefined,
  pinnedArticles: {},
  articles: [],
  page: 0,
  hasMore: true,
  loadingNews: false,
  setPage: (page: number) => set({ page }),
  loadArticle: (id: string) => {
    const sessionStore = useSessionStore.getState();
    set({
      currentArticle: undefined,
    });

    // The "no article" rescue below must only navigate while the user is still
    // looking at the news detail view. A response that lands after the user
    // already navigated elsewhere (e.g. a gleap://article link opened a help
    // center article) must not yank the widget to the home screen (#145642).
    const rescueToMenu = () => {
      if (useRouterStore.getState().currentPage === "newsdetails") {
        useRouterStore.getState().setPage("menu");
      }
    };

    var xhr = new XMLHttpRequest();
    xhr.open(
      "GET",
      sessionStore.apiUrl +
        "/outbound/news/shared/" +
        id +
        "?lang=" +
        useConfigStore.getState().getCurrentLanguage(),
    );
    if (sessionStore.session) {
      xhr.setRequestHeader("Api-Token", sessionStore.sdkKey);
      xhr.setRequestHeader("Gleap-Id", sessionStore.session.gleapId ?? "");
      xhr.setRequestHeader("Gleap-Hash", sessionStore.session.gleapHash ?? "");
    }
    xhr.onreadystatechange = function () {
      if (xhr.readyState != 4) {
        return;
      }
      try {
        // The endpoint answers 200 with an empty body when the article does not
        // exist or the visitor doesn't match its audience targeting. Treat an
        // empty/unparsable body (or any error status) as "no article" and leave
        // the detail view instead of getting stuck on the loading skeleton.
        const data =
          xhr.status == 200 && xhr.responseText
            ? JSON.parse(xhr.responseText)
            : null;
        if (data) {
          set({
            currentArticle: withArticleId(data) ?? undefined,
          });
        } else {
          rescueToMenu();
        }
      } catch (exp) {
        rescueToMenu();
      }
    };

    xhr.send();
  },
  loadPinnedArticle: (id: string) => {
    if (!id || id in get().pinnedArticles) {
      return;
    }
    // Seed the slot right away: it de-duplicates parallel requests and keeps
    // the card hidden until (and unless) the article actually resolves.
    set({ pinnedArticles: { ...get().pinnedArticles, [id]: null } });

    const sessionStore = useSessionStore.getState();
    var xhr = new XMLHttpRequest();
    xhr.open(
      "GET",
      sessionStore.apiUrl +
        "/outbound/news/shared/" +
        id +
        "?lang=" +
        useConfigStore.getState().getCurrentLanguage(),
    );
    if (sessionStore.session) {
      xhr.setRequestHeader("Api-Token", sessionStore.sdkKey);
      xhr.setRequestHeader("Gleap-Id", sessionStore.session.gleapId ?? "");
      xhr.setRequestHeader("Gleap-Hash", sessionStore.session.gleapHash ?? "");
    }
    xhr.onreadystatechange = function () {
      if (xhr.readyState != 4) {
        return;
      }
      try {
        // Same contract as loadArticle: the endpoint answers 200 with an
        // empty body when the article does not exist or the visitor doesn't
        // match its audience targeting — keep the null slot in that case.
        const data =
          xhr.status == 200 && xhr.responseText
            ? JSON.parse(xhr.responseText)
            : null;
        if (data) {
          set({
            pinnedArticles: {
              ...get().pinnedArticles,
              [id]: withArticleId(data),
            },
          });
        }
      } catch (exp) {}
    };

    xhr.send();
  },
  loadNews() {
    const sessionStore = useSessionStore.getState();
    const page = get().page;

    set({ loadingNews: true });

    const gleapId = sessionStore?.session?.gleapId ?? "";
    const gleapHash = sessionStore?.session?.gleapHash ?? "";

    var xhr = new XMLHttpRequest();
    xhr.open(
      "GET",
      sessionStore.apiUrl +
        "/outbound/news/shared?gleapId=" +
        gleapId +
        "&gleapHash=" +
        gleapHash +
        "&lang=" +
        useConfigStore.getState().getCurrentLanguage() +
        "&page=" +
        page,
    );
    if (sessionStore.session) {
      xhr.setRequestHeader("Api-Token", sessionStore.sdkKey);
      xhr.setRequestHeader("Gleap-Id", gleapId);
      xhr.setRequestHeader("Gleap-Hash", gleapHash);
    }
    xhr.onreadystatechange = function () {
      if (xhr.readyState == 4) {
        if (xhr.status === 200 && xhr.responseText) {
          try {
            const data = JSON.parse(xhr.responseText);
            if (data) {
              const hasArticles = get().articles?.length > 0;
              set({ page: page + 1 });

              // De-duplicate by id when appending: paginated pages can overlap
              // by one item at the boundary, so the same news must not be added
              // twice.
              const existing = hasArticles ? get().articles : [];
              const existingIds = new Set(existing.map((a) => a.id));
              const newArticles = data.filter(
                (a: Article) => !existingIds.has(a.id),
              );

              set({
                articles: [...existing, ...newArticles],
                loadingNews: false,
                hasMore: data.length === 10,
              });

              if (!hasArticles) {
                set({
                  latestArticle: data.length > 0 ? data[0] : undefined,
                });
              }
              return;
            }
          } catch (exp) {}
        }
        set({ loadingNews: false });
      }
    };

    xhr.send();
  },
}));
